import { useState } from "react";
import { Link } from "react-router-dom";
import { C } from "../constants/theme";
import { useSiteContent } from "../context/SiteContentContext";
import FadeIn from "../components/ui/FadeIn";
import TiltCard from "../components/ui/TiltCard";
import RainbowDivider from "../components/ui/RainbowDivider";

const MODULES = [
  { icon:"📗", color:"#4EA8DE", title:"Qaida for Kids",
    body:"The building blocks — letters, sounds, and joining them together — the foundation everything else stands on.",
    why:"A child who reads the Qaida with confidence never fears opening the Mus'haf later." },
  { icon:"📖", color:"#9B59B6", title:"Nazra for Kids",
    body:"Reading the Quran directly from the Mus'haf, fluently and with proper pronunciation.",
    why:"Nazra is where the Qaida becomes real — the Quran in front of them, not just letters on a page." },
  { icon:"🎙️", color:"#D9534F", title:"Tajweed for Kids",
    body:"The rules of correct recitation — how each letter is pronounced, where to pause, how it flows.",
    why:"Correct Tajweed isn't decoration — it's how the Quran has been protected letter by letter, generation to generation." },
  { icon:"🔍", color:C.mint,    title:"Tafseer for Kids",
    body:"Understanding the meaning behind the verses, explained in language a child can actually hold onto.",
    why:"A verse recited without understanding is a melody. A verse understood is a compass." },
  { icon:"🌙", color:C.navy,    title:"Seerah for Kids",
    body:"The life of the Prophet ﷺ, told the way a story should be told — with feeling, not just facts.",
    why:"Children don't remember dates. They remember the Prophet ﷺ being kind to a child — and that memory shapes who they become." },
  { icon:"🕋", color:"#D9534F", title:"Islamic Aqeeda for Kids",
    body:"The foundations of belief — who Allah is, what a Muslim believes, and why — taught at its source.",
    why:"A child who knows what they believe, and why, can't easily be shaken by what the world tells them later." },
  { icon:"🗣️", color:"#7396DD", title:"Arabic as a Language for Kids",
    body:"Arabic taught as a living language, not just letters to memorise — so the Quran is never foreign to them.",
    why:"When a child understands the words they're reciting, recitation turns into conversation with Allah." },
  { icon:"📚", color:C.yellow,  title:"Prophet Stories for Kids",
    body:"The stories of the Prophets — their trials, their patience, their trust in Allah — brought to life for young hearts.",
    why:"Every Prophet's story is a child's first lesson in what courage actually looks like." },
  { icon:"🌟", color:"#8B6BE8", title:"Own Your Muslim Identity",
    body:"Helping every child feel proud, secure, and unapologetic about who they are — a Muslim, first and always.",
    why:"A child who is confident in their identity doesn't need to shrink themselves to belong anywhere." },
];

const WHATSAPP_NUMBER = "923390002106";

const QuranForKids = () => {
  const { content } = useSiteContent();
  const ct = content.contact;
  const whatsappNumber = (ct?.whatsappLink || `https://wa.me/${WHATSAPP_NUMBER}`).replace(/\D/g, "").slice(-12) || WHATSAPP_NUMBER;
  const [active, setActive] = useState(0);

  return (
    <div style={{ paddingTop: 72 }}>
      {/* ── Hero ── */}
      <section style={{ background:`linear-gradient(160deg,${C.navy} 0%,#0f2a70 100%)`, padding:"100px 6vw 90px", position:"relative", overflow:"hidden", minHeight:"48vh", display:"flex", alignItems:"center" }}>
        <div style={{ position:"absolute", top:-80, right:-80, width:450, height:450, borderRadius:"50%", background:`${C.yellow}12`, pointerEvents:"none" }}/>
        <div style={{ maxWidth:1100, margin:"0 auto", position:"relative", zIndex:2 }}>
          <Link to="/programs" style={{ display:"inline-flex", alignItems:"center", gap:6, fontFamily:"Nunito", fontWeight:700, fontSize:12.5, color:"rgba(255,255,255,0.75)", textDecoration:"none", marginBottom:16 }}>
            ← Back to All Programmes
          </Link>
          <div style={{ display:"inline-flex", alignItems:"center", gap:8, background:`${C.yellow}22`, border:`1px solid ${C.yellow}50`, color:C.yellow, fontFamily:"Nunito", fontWeight:700, fontSize:11, letterSpacing:"0.15em", padding:"6px 16px", borderRadius:100, textTransform:"uppercase", marginBottom:20 }}>A Dedicated Programme · Fully Online</div>
          <h1 style={{ fontFamily:"Fredoka One", fontSize:"clamp(28px,5vw,54px)", color:C.white, lineHeight:1.1, margin:"0 0 18px", maxWidth:680 }}>
            Quran for Kids.
          </h1>
          <p style={{ fontFamily:"Lora", fontStyle:"italic", fontSize:"clamp(14px,2vw,19px)", color:"rgba(255,255,255,0.85)", maxWidth:600, lineHeight:1.82, margin:"0 0 32px" }}>
            Nine courses, all delivered online, woven into one journey — from the first letter of the Qaida to a child who carries their Muslim identity with quiet confidence.
          </p>
          <a href={`https://wa.me/${whatsappNumber}?text=${encodeURIComponent("Assalamu Alaikum! I'd like to know more about the Quran for Kids programme.")}`} target="_blank" rel="noopener noreferrer" style={{
            background:C.yellow, color:C.navy, textDecoration:"none", fontFamily:"Nunito", fontWeight:800, fontSize:15,
            padding:"16px 32px", borderRadius:100, display:"inline-block",
          }}>Ask About This Programme</a>
        </div>
      </section>

      {/* ── Courses spotlight ── */}
      <section style={{ background:C.cream, padding:"90px 6vw" }}>
        <div style={{ maxWidth:1000, margin:"0 auto" }}>
          <FadeIn>
            <div style={{ textAlign:"center", marginBottom:44 }}>
              <div style={{ fontFamily:"Nunito", fontWeight:700, fontSize:12, color:C.coral, letterSpacing:"0.15em", textTransform:"uppercase", marginBottom:12 }}>What's Inside</div>
              <h2 style={{ fontFamily:"Fredoka One", fontSize:"clamp(24px,3.5vw,40px)", color:C.navy, margin:0 }}>Nine Courses, Each One Whole.</h2>
              <RainbowDivider />
              <p style={{ fontFamily:"Nunito", fontSize:14, color:C.muted, maxWidth:560, margin:"14px auto 0" }}>
                Tap any course to see why it matters.
              </p>
            </div>
          </FadeIn>

          <FadeIn delay={0.06}>
            <div style={{ display:"flex", gap:8, flexWrap:"wrap", justifyContent:"center", marginBottom:32 }}>
              {MODULES.map(({ icon, color, title }, i) => (
                <button
                  key={title}
                  onClick={() => setActive(i)}
                  style={{
                    display:"flex", alignItems:"center", gap:7,
                    background: active === i ? color : C.white,
                    color: active === i ? C.white : color,
                    border:`2px solid ${color}`,
                    fontFamily:"Nunito", fontWeight:800, fontSize:12.5,
                    padding:"9px 16px", borderRadius:100, cursor:"pointer", transition:"all 0.2s",
                    boxShadow: active === i ? `0 4px 14px ${color}45` : "none",
                  }}
                ><span>{icon}</span> {title}</button>
              ))}
            </div>
          </FadeIn>

          <FadeIn delay={0.1}>
            <TiltCard maxTilt={2} style={{
              background:C.white, borderRadius:28, padding:"44px 40px",
              boxShadow:"0 14px 40px rgba(0,0,0,0.08)",
              borderTop:`6px solid ${MODULES[active].color}`,
              transition:"border-top-color 0.3s ease",
            }}>
              <div style={{ display:"flex", gap:24, alignItems:"flex-start", flexWrap:"wrap" }}>
                <div style={{
                  width:76, height:76, borderRadius:22, flexShrink:0,
                  background:`${MODULES[active].color}18`,
                  display:"flex", alignItems:"center", justifyContent:"center", fontSize:36,
                }}>{MODULES[active].icon}</div>
                <div style={{ flex:"1 1 320px" }}>
                  <div style={{ fontFamily:"Fredoka One", fontSize:26, color:MODULES[active].color, marginBottom:10, lineHeight:1.2 }}>
                    {MODULES[active].title}
                  </div>
                  <p style={{ fontFamily:"Nunito", fontSize:15.5, color:C.text, lineHeight:1.8, margin:"0 0 16px" }}>
                    {MODULES[active].body}
                  </p>
                  <div style={{ borderLeft:`4px solid ${MODULES[active].color}`, paddingLeft:16 }}>
                    <p style={{ fontFamily:"Lora", fontStyle:"italic", fontSize:15, color:MODULES[active].color, lineHeight:1.7, margin:0 }}>
                      "{MODULES[active].why}"
                    </p>
                  </div>
                </div>
              </div>
            </TiltCard>
          </FadeIn>

          <div style={{ textAlign:"center", fontFamily:"Nunito", fontSize:12.5, color:C.muted, marginTop:18 }}>
            {active + 1} of {MODULES.length} — tap another course above to explore it
          </div>
        </div>
      </section>

      {/* ── Why one journey ── */}
      <section style={{ background:C.navy, padding:"90px 6vw" }}>
        <div style={{ maxWidth:700, margin:"0 auto", textAlign:"center" }}>
          <FadeIn>
            <div style={{ fontSize:40, marginBottom:16 }}>🤲</div>
            <h2 style={{ fontFamily:"Fredoka One", fontSize:"clamp(22px,3.5vw,34px)", color:C.white, margin:"0 0 18px" }}>Not Nine Subjects. One Child.</h2>
            <p style={{ fontFamily:"Nunito", fontSize:15, color:"rgba(255,255,255,0.75)", lineHeight:1.9, margin:0 }}>
              Qaida, Nazra, and Tajweed teach a child to read the Quran correctly. Tafseer and Arabic teach them to understand it. Seerah and Prophet Stories teach them to love it, through the people who lived it. Aqeeda gives them the "why" underneath it all — and every course points toward the same place: a child who owns their Muslim identity, not because they were told to, but because they understand it.
            </p>
          </FadeIn>
        </div>
      </section>

      {/* ── CTA ── */}
      <section style={{ background:C.cream, padding:"80px 6vw" }}>
        <div style={{ maxWidth:560, margin:"0 auto", textAlign:"center" }}>
          <FadeIn>
            <h2 style={{ fontFamily:"Fredoka One", fontSize:"clamp(22px,3.5vw,34px)", color:C.navy, margin:"0 0 14px" }}>Interested for Your Child?</h2>
            <p style={{ fontFamily:"Nunito", fontSize:15, color:C.muted, lineHeight:1.75, marginBottom:32 }}>Message us to find out how Quran for Kids fits alongside — or as part of — your child's learning journey.</p>
            <div style={{ display:"flex", gap:14, justifyContent:"center", flexWrap:"wrap" }}>
              <a href={`https://wa.me/${whatsappNumber}?text=${encodeURIComponent("Assalamu Alaikum! I'd like to know more about the Quran for Kids programme.")}`} target="_blank" rel="noopener noreferrer" style={{ background:"#25D366", color:C.white, textDecoration:"none", fontFamily:"Nunito", fontWeight:800, fontSize:15, padding:"16px 36px", borderRadius:100, display:"inline-block" }}>💬 Ask on WhatsApp</a>
              <Link to="/admissions" style={{ background:"transparent", color:C.navy, textDecoration:"none", fontFamily:"Nunito", fontWeight:700, fontSize:15, padding:"16px 36px", borderRadius:100, border:`2px solid ${C.navy}`, display:"inline-block" }}>See Admissions</Link>
            </div>
          </FadeIn>
        </div>
      </section>
    </div>
  );
};

export default QuranForKids;
